import React from 'react';
import { withRouter } from 'react-router';
import numeral from 'numeral';
import merge from 'lodash/merge';

import _ from '@ihme/common/locale';
import { ChartZoomProvider, Clearfix, MapChartZoomControls } from '@ihme/common/web/components';
import { MapChart as BaseMapChart} from '@ihme/common/web/components';
import styled from '@ihme/common/theme/styled';

import config from '../../../config';
import { TRENDS_PATH } from '../../../router/paths';

import { renderChartCopyright } from '../../../utility/chart-copyright';
import echartsTheme from '../../../theme/echarts';
import { pngIcon } from '../../../theme/icons';

const StyledMapChartZoomControls = styled(MapChartZoomControls)(() => ({
    marginTop: 10,
    marginBottom: 10,
    float: 'right',
    'button + button': {
        marginLeft: 10,
    },
}));

type Props = {};

class MapChart extends React.Component<Props> {
    renderTitle = ({ filters, sliderValue }) =>
        _('global_impact_chart_title', {
            cause: _(`cause_${filters.cause_id}`),
            year: sliderValue,
        });

    renderSubtitle = ({ filters }) =>
        _('global_impact_chart_subtitle', {
            ageGroup: _(`age_group_${filters.age_group_id}`),
            measure: _(`measure_${filters.measure_id}`),
            metric: _(`metric_${filters.metric_id}`),
        });

    renderTooltip = params => {
        const locationName = this.getLocalizedFilter([params.data.id], 'location_', '');

        return `<div style="color:black;">
            <b>
                <span style="color: #EF3828;">${locationName}:</span>
                ${numeral(params.value)
            .format('0,0')}
            </b>
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            <a
                onClick="window.onLocationDetails(${params.data.id});"
                class="btn btn-default">
                &gt;
            </a>
        </div>`;
    }

    getSaveFilename = ({
        filters: { cause_id, measure_id, metric_id, age_group_id, location_id },
        sliderValue
    }) =>
        'Trends Chart' +
        this.getLocalizedFilter(cause_id, 'cause_', ', ') +
        this.getLocalizedFilter(measure_id, 'measure_', ', ') +
        this.getLocalizedFilter(metric_id, 'long_metric_', ', ') +
        this.getLocalizedFilter(location_id, 'location_', ' in ') +
        this.getLocalizedFilter(age_group_id, 'age_group_', ', ') +
        ', ' +
        sliderValue;

    getLocalizedFilter = (
        value: string | number,
        localePrefix: string,
        prefix: string = ''
    ): string =>
        value
            ? prefix +
              value
                  .toString()
                  .split(',')
                  .map(id => _(`${localePrefix}${id}`))
                  .join(', ')
            : '';

    render() {
        const {
            history,
            location,
        } = this.props;

        window.onLocationDetails = locationId => {
            history.push({
                pathname: TRENDS_PATH,
                search: `${location.search}&location_id=${locationId}`,
            });
        };

        return (
            <ChartZoomProvider>
                <StyledMapChartZoomControls/>
                <Clearfix/>
                <BaseMapChart
                    theme={merge(echartsTheme,
                        {
                            map: {
                                roam: true,
                                silent: false,
                            },
                        }
                    )}
                    height={600}
                    resizeChartOnWindowResize={false}
                    enableSliderWithFilterKey="year"
                    initialSliderValue={config.defaultGlobalYear}
                    renderTooltip={this.renderTooltip}
                    renderTitle={this.renderTitle}
                    renderSubtitle={this.renderSubtitle}
                    renderGraphic={renderChartCopyright}
                    saveAsImage={{
                        visible: true,
                        filename: this.getSaveFilename,
                        icon: pngIcon,
                    }}
                />
            </ChartZoomProvider>
        );
    }
}

export default withRouter(MapChart);
